import React, { useState, useEffect, useRef } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Nav, NavItem, NavLink, TabContent, 
    TabPane, Modal, ModalHeader, ModalBody, FormGroup, Label,
    Form, Spinner
} from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { APIClient } from '../../helpers/api_helper';
import SweetAlert from 'react-bootstrap-sweetalert';
import AWS from 'aws-sdk';

const AdminUsers = () => {
    document.title = "Admin Users";
    
    const [activeTab, setActiveTab] = useState('users');
    const [adminUsers, setAdminUsers] = useState([]);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [roleModal, setRoleModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleFormData, setRoleFormData] = useState({
        role: '',
        userContactMasked: true,
        access: {
            AppTextSettings: '10',
            AdminUsers: '10',
            AppConfig: '11',
            Users: '11',
            DashBoard: '10',
            DateLogs: '10',
            Gender: '10',
            LikeLogs: '11',
            MatchLogs: '11',
            ModerationMedia: '11',
            Pronoun: '11',
            Posts: '11',
            AppSubscriptionPlans: '11',
            TokenPlans: '10',
            ReferralEarningLogs: '10',
            ReportedIssue: '10',
            ReviewAndRatings: '10',
            LockedPosts: '10',
            Logs: '10',
            SendUpdate: '10',
            UserMetaData: '11',
            VirtualGift: '11'
        }
    });
    const [resetPasswordModal, setResetPasswordModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [passwords, setPasswords] = useState({
        password: '',
        confirmPassword: ''
    });
    const [resetLoading, setResetLoading] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [sessionModal, setSessionModal] = useState(false);
    const [sessionLogs, setSessionLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [sessionSearch, setSessionSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [sweetAlert, setSweetAlert] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [addUserModal, setAddUserModal] = useState(false);
    const [addUserForm, setAddUserForm] = useState({
        username: '',
        email: '',
        roleId: '',
        password: '',
        image: ''
    });
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const [cognitoData, setCognitoData] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);

    // Filtered roles computation
    const filteredRoles = Array.isArray(roles) ? roles.filter(role => 
        role.role.toLowerCase().includes(searchText.toLowerCase())
    ) : [];

    // Add this helper function at the top of the component
    const accessOptions = {
        AppTextSettings: "App Text Settings",
        AdminUsers: "Admin Users",
        AppConfig: "App Config",
        Users: "Users",
        DashBoard: "Dashboard",
        DateLogs: "Date Logs",
        Gender: "Gender",
        LikeLogs: "Like Logs",
        MatchLogs: "Match Logs",
        ModerationMedia: "Moderation Media",
        Pronoun: "Pronoun",
        Posts: "Posts",
        AppSubscriptionPlans: "App Subscription Plans",
        TokenPlans: "Token Plans",
        ReferralEarningLogs: "Referral Earning Logs",
        ReportedIssue: "Reported Issue",
        ReviewAndRatings: "Review And Ratings",
        LockedPosts: "Locked Posts",
        Logs: "Logs",
        SendUpdate: "Send Update",
        UserMetaData: "User Meta Data",
        VirtualGift: "Virtual Gift"
    };

    // Update the default roleFormData state with all access options
    const defaultAccess = {
        AppTextSettings: '10',
        AdminUsers: '10',
        AppConfig: '11',
        Users: '11',
        DashBoard: '10',
        DateLogs: '10',
        Gender: '10',
        LikeLogs: '11',
        MatchLogs: '11',
        ModerationMedia: '11',
        Pronoun: '11',
        Posts: '11',
        AppSubscriptionPlans: '11',
        TokenPlans: '10',
        ReferralEarningLogs: '10',
        ReportedIssue: '10',
        ReviewAndRatings: '10',
        LockedPosts: '10',
        Logs: '10',
        SendUpdate: '10',
        UserMetaData: '11',
        VirtualGift: '11'
    };

    // Add this filtering function
    const filteredUsers = adminUsers.filter(user => {
        const searchLower = searchText.toLowerCase();
        return (
            user.userName.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.role.toLowerCase().includes(searchLower)
        );
    });

    // Fetch functions
    const fetchAdminUsers = async () => {
        try {
            setLoading(true);
            const response = await APIClient.get('/python/adminUsers/');
            console.log('Admin users response:', response); // Debug log
            // Check if response.result exists and is an array
            if (response.status === 200 && response.data.result && Array.isArray(response.data.result)) {
                setAdminUsers(response.data.result);
            } else {
                console.error('Invalid admin users data format:', response);
                setAdminUsers([]);
            }
        } catch (error) {
            console.error('Error fetching admin users:', error);
            toast.error('Failed to fetch admin users');
            setAdminUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchDeletedUsers = async () => {
        try {
            setLoading(true);
            const response = await APIClient.get('/python/adminUsers/?status=3');
            console.log('Deleted users response:', response); // Debug log
            // Check if response.data.result exists and is an array
            if (response && response.data.result && Array.isArray(response.data.result)) {
                setDeletedUsers(response.data.result);
            } else {
                console.error('Invalid deleted users data format:', response);
                setDeletedUsers([]);
            }
        } catch (error) {
            console.error('Error fetching deleted users:', error);
            toast.error('Failed to fetch deleted users');
            setDeletedUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await APIClient.get('/python/adminRoles/');
            console.log('Roles response:', response); // Debug log
            // Check if response.data exists and is an array
            if (response && response.data.data && Array.isArray(response.data.data)) {
                setRoles(response.data.data);
            } else {
                console.error('Invalid roles data format:', response);
                setRoles([]);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
            toast.error('Failed to fetch roles');
            setRoles([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableRoles = async () => {
        try {
            const response = await APIClient.get('/python/adminRoles/');
            if (response.status === 200 && response.data) {
                setAvailableRoles(response.data.data || []);
            }
        } catch (error) {
            toast.error('Failed to fetch roles');
            console.error('Error fetching roles:', error);
        }
    };

    useEffect(() => {
        if (activeTab === 'users') fetchAdminUsers();
        if (activeTab === 'roles') fetchRoles();
        if (activeTab === 'deleted') fetchDeletedUsers();
    }, [activeTab]);

    // Role management functions
    const handleAddRole = async () => {
        try {
            setLoading(true);
            const payload = {
                role: roleFormData.role,
                userContactMasked: roleFormData.userContactMasked,
                access: roleFormData.access
            };
            
            console.log('Adding role with payload:', payload);
            
            const response = await APIClient.create('/python/addAdminRoles/', payload);
            console.log('Add role response:', response);
            
            if (response.status === 200) {
                toast.success('Role added successfully');
                setRoleModal(false);
                fetchRoles();
            } else {
                throw new Error(response.message || 'Failed to add role');
            }
        } catch (error) {
            console.error('Error adding role:', error);
            toast.error(error.message || 'Failed to add role');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateRole = async () => {
        try {
            setLoading(true);
            const payload = {
                _id: selectedRole._id,
                ...roleFormData
            };
            
            const response = await APIClient.patch('/python/updateAdminRoles/', payload);
            
            if (response.status === 200) {
                toast.success('Role updated successfully');
                setRoleModal(false);
                fetchRoles();
            } else {
                throw new Error(response.message || 'Failed to update role');
            }
        } catch (error) {
            console.error('Error updating role:', error);
            toast.error(error.message || 'Failed to update role');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRole = async (roleId) => {
        try {
            setLoading(true);
            const payload = {
                id: [roleId]
            };
            
            const response = await APIClient.patch('/python/adminRolesAction/', payload);
            
            if (response.status === 200) {
                toast.success('Role deleted successfully');
                fetchRoles();
            } else {
                throw new Error(response.message || 'Failed to delete role');
            }
        } catch (error) {
            console.error('Error deleting role:', error);
            toast.error(error.message || 'Failed to delete role');
        } finally {
            setLoading(false);
        }
    };

    // Add this function to handle password reset
    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        // Validate passwords
        if (passwords.password !== passwords.confirmPassword) {
            toast.error("Passwords don't match!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            return;
        }

        if (passwords.password.length < 5) {
            toast.error("Password must be at least 5 characters long!", {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
            return;
        }

        try {
            setResetLoading(true);
            const response = await APIClient.patch('/python/adminUserResetPassword/', {
                userId: selectedUserId,
                newPassword: passwords.password
            });

            if (response.status === 200) {
                toast.success("Password reset successfully", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                setResetPasswordModal(false);
                // Clear the form
                setPasswords({ password: '', confirmPassword: '' });
            } else {
                throw new Error(response.message || 'Failed to reset password');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to reset password', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setResetLoading(false);
        }
    };

    // Update the handleViewSessions function
    const handleViewSessions = async (userId) => {
        try {
            setLoadingLogs(true);
            const response = await APIClient.get(`/python/adminUserSessions/?userId=${userId}`);
            if (response.status === 200 && response.data) {
                setSessionLogs(response.data.result || []); // Add null check and default to empty array
                setSessionModal(true);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to fetch session logs', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoadingLogs(false);
        }
    };

    // Add checkbox handlers
    const handleSelectAll = (e) => {
        setSelectAll(e.target.checked);
        if (e.target.checked) {
            setSelectedUsers(adminUsers.map(user => user._id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleSelectUser = (userId) => {
        if (selectedUsers.includes(userId)) {
            setSelectedUsers(selectedUsers.filter(id => id !== userId));
            setSelectAll(false);
        } else {
            setSelectedUsers([...selectedUsers, userId]);
            if (selectedUsers.length + 1 === adminUsers.length) {
                setSelectAll(true);
            }
        }
    };

    // Add this function to filter and paginate session logs
    const getFilteredSessionLogs = () => {
        if (!Array.isArray(sessionLogs)) return [];
        
        const filtered = sessionLogs.filter(log => 
            log.action.toLowerCase().includes(sessionSearch.toLowerCase())
        );

        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filtered.slice(startIndex, endIndex);
    };

    // Add these action handler functions
    const handleUserAction = async (action, status) => {
        try {
            setActionLoading(true);
            const response = await APIClient.patch('/python/adminUsersAction/', {
                deleteStatus: status,
                id: selectedUsers
            });

            if (response.status === 200) {
                toast.success("Admin User Updated", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: "colored"
                });
                // Refresh the user list
                fetchAdminUsers();
            }
        } catch (error) {
            toast.error(error.message || 'Failed to update user status', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setActionLoading(false);
            setSweetAlert(null);
        }
    };

    // Add confirmation dialog functions
    const showDeleteConfirm = () => {
        setSweetAlert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="danger"
                title="Are you sure?"
                onConfirm={() => handleUserAction('delete', '3')}
                onCancel={() => setSweetAlert(null)}
                cancelBtnBsStyle="default"
            >
                Are you sure you want to delete the selected users?
            </SweetAlert>
        );
    };

    const showSuspendConfirm = () => {
        setSweetAlert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, suspend it!"
                confirmBtnBsStyle="warning"
                title="Are you sure?"
                onConfirm={() => handleUserAction('suspend', '2')}
                onCancel={() => setSweetAlert(null)}
                cancelBtnBsStyle="default"
            >
                Are you sure you want to suspend the selected users?
            </SweetAlert>
        );
    };

    const showActivateConfirm = () => {
        setSweetAlert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, activate it!"
                confirmBtnBsStyle="success"
                title="Are you sure?"
                onConfirm={() => handleUserAction('activate', '1')}
                onCancel={() => setSweetAlert(null)}
                cancelBtnBsStyle="default"
            >
                Are you sure you want to activate the selected users?
            </SweetAlert>
        );
    };

    const MEDIA_URL = process.env.REACT_APP_MEDIA_URL?.replace(/\/$/, '');

    const getImageUrl = (url) => {
        // If no URL provided or URL doesn't start with "image/", use default image
        if (!url || !url.startsWith('image/')) {
            // Return path to default profile image
            return require('../../assets/images/users/default.jpg');
        }
        
        // If URL starts with http, return as is
        if (url.startsWith('http')) return url;
        
        // Otherwise, combine with media URL
        return `${MEDIA_URL}/${url.replace(/^\//, '')}`;
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // File validation
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size should be less than 2MB', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
            return;
        }

        try {
            setUploadLoading(true);

            // 1. Get Cognito Token
            const cognitoResponse = await APIClient.get('/cognitoToken');
            
            if (!cognitoResponse.data || !cognitoResponse.data.data) {
                toast.error('Failed to get upload configuration', {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                });
                throw new Error('Failed to get upload configuration');
            }

            const cognitoData = cognitoResponse.data;
            setCognitoData(cognitoData.data);

            // 2. Configure AWS
            AWS.config.update({
                region: cognitoData.data.region,
                credentials: new AWS.CognitoIdentityCredentials({
                    IdentityId: cognitoData.data.IdentityId,
                    Logins: {
                        'cognito-identity.amazonaws.com': cognitoData.data.Token
                    }
                })
            });

            // Wait for credentials to be refreshed
            await new Promise((resolve, reject) => {
                AWS.config.credentials.refresh((error) => {
                    if (error) {
                        reject(error);
                    } else {
                        resolve();
                    }
                });
            });

            // 3. Create S3 instance with credentials
            const s3 = new AWS.S3({
                apiVersion: '2006-03-01',
                params: { Bucket: cognitoData.data.imageBucket },
                region: cognitoData.data.region
            });

            const fileName = `image/${file.name}`;

            // 4. Upload to S3
            const uploadParams = {
                Bucket: cognitoData.data.imageBucket,
                Key: fileName,
                Body: file,
                ContentType: file.type,
                ACL: 'public-read'
            };

            await s3.upload(uploadParams).promise();

            // Update state with the uploaded file info
            setSelectedFile(file);
            setAddUserForm({
                ...addUserForm,
                image: fileName
            });
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            toast.success('Image uploaded successfully');

        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.message || 'Failed to upload image', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
            });
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('drag-over');
        const file = event.dataTransfer.files[0];
        if (file) {
            fileInputRef.current.files = event.dataTransfer.files;
            handleFileSelect({ target: { files: [file] } });
        }
    };

    // Update the handleAddUser function
    const handleAddUser = async (e) => {
        e.preventDefault();
        try {
            setAddUserLoading(true);
            // Format payload to match API requirements
            const payload = {
                username: addUserForm.username,
                email: addUserForm.email,
                password: addUserForm.password,
                roleId: addUserForm.roleId,
                image: addUserForm.image || ''
            };
            
            const response = await APIClient.create('/python/addAdminUsers/', payload);
            
            if (response.status === 200) {
                toast.success('Admin User Added');
                setAddUserModal(false);
                // Reset form and file states
                setAddUserForm({ username: '', email: '', roleId: '', password: '', image: '' });
                setSelectedFile(null);
                setImagePreview(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                fetchAdminUsers(); // Refresh the users list
            }
        } catch (error) {
            toast.error(error.message || 'Failed to add user');
            console.error('Error adding user:', error);
        } finally {
            setAddUserLoading(false);
        }
    };

    // Add this style block right before the return statement
    const styles = `
        /* Reset any transform/transition effects */
        .header-item img,
        .btn-topbar img {
            transform: none !important;
            transition: none !important;
        }

        /* Ensure icons maintain aspect ratio */
        .header-item img,
        .btn-topbar img {
            object-fit: contain !important;
            max-width: 20px !important;
            max-height: 20px !important;
            width: auto !important;
            height: auto !important;
        }

        /* Specific fix for profile images */
        .rounded-circle.header-profile-user {
            width: 32px !important;
            height: 32px !important;
            object-fit: cover !important;
        }
    `;

    return (
        <React.Fragment>
            <style>{styles}</style>
            <div className="page-content">
                <Container fluid>
                        <BreadCrumb title="Admin Users" pageTitle="Settings" />
                        
                            <Card>
                            <CardHeader>
                                <Nav tabs>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'users' ? 'active' : ''}
                                            onClick={() => setActiveTab('users')}
                                        >
                                            Admin Users
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'roles' ? 'active' : ''}
                                            onClick={() => setActiveTab('roles')}
                                        >
                                            Roles
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'deleted' ? 'active' : ''}
                                            onClick={() => setActiveTab('deleted')}
                                        >
                                            Deleted Users
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </CardHeader>
                            <CardBody>
                                <TabContent activeTab={activeTab}>
                                    {/* Users Tab */}
                                    <TabPane tabId="users">
                                        <div className="card">
                                            <div className="card-header border-0">
                                                <Row className="g-4">
                                                    <Col className="col-sm">
                                                        <div className="d-flex justify-content-sm-between align-items-center">
                                                            <h5 className="card-title mb-0">Admin Users</h5>
                                                            <div className="search-box ms-2">
                                                                <input
                                                                    type="text"
                                                                    className="form-control search"
                                                                    placeholder="Search for users..."
                                                                    value={searchText}
                                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    />
                                                </div>
                                                    </div>
                                                    </Col>
                                                    <Col className="col-sm-auto">
                                                        <div className="d-flex flex-wrap gap-2">
                                                            <Button
                                                                color="danger"
                                                                className="add-btn"
                                                                onClick={showDeleteConfirm}
                                                                disabled={selectedUsers.length === 0 || actionLoading}
                                                            >
                                                                {actionLoading ? <Spinner size="sm" /> : 'Delete'}
                                                            </Button>
                                        <Button 
                                            color="warning" 
                                                                className="add-btn"
                                                                onClick={showSuspendConfirm}
                                                                disabled={selectedUsers.length === 0 || actionLoading}
                                                            >
                                                                {actionLoading ? <Spinner size="sm" /> : 'Suspend'}
                                                            </Button>
                                                            <Button
                                                                color="success"
                                                                className="add-btn"
                                                                onClick={showActivateConfirm}
                                                                disabled={selectedUsers.length === 0 || actionLoading}
                                                            >
                                                                {actionLoading ? <Spinner size="sm" /> : 'Activate'}
                                                            </Button>
                                                            <Button
                                                                color="primary"
                                                                className="add-btn"
                                                                onClick={() => {
                                                                    fetchAvailableRoles();
                                                                    setAddUserModal(true);
                                                                }}
                                                            >
                                                                <i className="ri-add-line align-bottom me-1"></i>Add
                                        </Button>
                                    </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>

                                        {loading ? (
                                            <div className="text-center">
                                                <Spinner color="primary" />
                                            </div>
                                        ) : (
                                    <div className="table-responsive">
                                                <Table className="align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                            <th scope="col">
                                                                <div className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={selectAll}
                                                                        onChange={handleSelectAll}
                                                                    />
                                                                </div>
                                                            </th>
                                                                <th scope="col">SL.No</th>
                                                                <th scope="col">Name</th>
                                                                <th scope="col">Email</th>
                                                                <th scope="col">Role</th>
                                                                <th scope="col">Sessions</th>
                                                                <th scope="col">Status</th>
                                                                <th scope="col">Created On</th>
                                                                <th scope="col">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                        {filteredUsers.map((user, index) => (
                                                            <tr key={user._id}>
                                                                <td>
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={selectedUsers.includes(user._id)}
                                                                            onChange={() => handleSelectUser(user._id)}
                                                                        />
                                                                    </div>
                                                                </td>
                                                        <td>{index + 1}</td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <div className="flex-shrink-0" style={{ width: '32px', height: '32px' }}>
                                                                            <img 
                                                                                src={getImageUrl(user.image)} 
                                                                                alt={`${user.userName}'s avatar`}
                                                                                className="rounded-circle w-100 h-100"
                                                                                style={{ objectFit: 'cover' }}
                                                                                onError={(e) => {
                                                                                    e.target.onerror = null;
                                                                                    e.target.src = require('../../assets/images/users/avatar-1.jpg');
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <div className="flex-grow-1">
                                                                            {user.userName}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>{user.email}</td>
                                                                <td>{user.role}</td>
                                                                <td>
                                                                    <Button
                                                                        color="info"
                                                                        size="sm"
                                                                        onClick={() => handleViewSessions(user._id)}
                                                                    >
                                                                        {user.noOfSessions}
                                                                    </Button>
                                                                </td>
                                                                <td>
                                                                    <span className={`badge ${
                                                                        user.statusCode === 1 ? 'bg-success-subtle text-success' :
                                                                        user.statusCode === 2 ? 'bg-warning-subtle text-warning' :
                                                                        'bg-danger-subtle text-danger'
                                                                    }`}>
                                                                        {user.statusText}
                                                            </span>
                                                        </td>
                                                                <td>{moment(user.createdOn).format('DD MMM YYYY')}</td>
                                                                <td>
                                                                    <Button
                                                                        color="soft-primary"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setSelectedUserId(user._id);
                                                                            setResetPasswordModal(true);
                                                                        }}
                                                                    >
                                                                        Reset Password
                                                                    </Button>
                                                        </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </TabPane>

                                    {/* Roles Tab */}
                                    <TabPane tabId="roles">
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <Input
                                                type="search"
                                                placeholder="Search roles..."
                                                value={searchText}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                style={{ width: '200px' }}
                                            />
                                            <Button
                                                color="primary"
                                                onClick={() => {
                                                    setSelectedRole(null);
                                                    setRoleFormData({
                                                        role: '',
                                                        userContactMasked: true,
                                                        access: { ...defaultAccess } // Use the default access object
                                                    });
                                                    setRoleModal(true);
                                                }}
                                            >
                                                Add Role
                                            </Button>
                                        </div>
                                        <Table className="table-striped table-nowrap align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Role Name</th>
                                                    <th>Contact Masked</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRoles.map(role => (
                                                    <tr key={role._id}>
                                                        <td>{role.role}</td>
                                                        <td>
                                                            <i className={`ri-${role.userContactMasked ? 'check' : 'close'}-line`}></i>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                color="soft-primary"
                                                                className="btn-sm me-2"
                                                                onClick={() => {
                                                                    setSelectedRole(role);
                                                                    setRoleFormData(role);
                                                                    setRoleModal(true);
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                color="soft-danger"
                                                                className="btn-sm"
                                                                onClick={() => handleDeleteRole(role._id)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </TabPane>

                                    {/* Deleted Users Tab */}
                                    <TabPane tabId="deleted">
                                        <div className="table-responsive">
                                            <Table className="table-striped table-nowrap align-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Username</th>
                                                        <th>Email</th>
                                                        <th>Role</th>
                                                        <th>Sessions</th>
                                                        <th>Created On</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {deletedUsers.map(user => (
                                                        <tr key={user._id}>
                                                            <td>{user.userName}</td>
                                                            <td>{user.email}</td>
                                                            <td>{user.role}</td>
                                                            <td>{user.noOfSessions}</td>
                                                            <td>{moment(user.createdOn).format('DD MMM YYYY')}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </TabPane>
                                </TabContent>
                                </CardBody>
                            </Card>
                </Container>
                    </div>

                {/* Role Modal */}
                <Modal isOpen={roleModal} toggle={() => setRoleModal(false)} size="lg">
                    <ModalHeader toggle={() => setRoleModal(false)}>
                        {selectedRole ? 'Edit Role' : 'Add Role'}
                    </ModalHeader>
                    <ModalBody>
                        <Form onSubmit={(e) => {
                            e.preventDefault();
                            selectedRole ? handleUpdateRole() : handleAddRole();
                        }}>
                            <FormGroup>
                                <Label>Role Name</Label>
                                <Input
                                    type="text"
                                    value={roleFormData.role}
                                    onChange={(e) => setRoleFormData({
                                        ...roleFormData,
                                        role: e.target.value
                                    })}
                                    required
                                />
                            </FormGroup>
                            <FormGroup check className="mb-4">
                                <Input
                                    type="checkbox"
                                    checked={roleFormData.userContactMasked}
                                    onChange={(e) => setRoleFormData({
                                        ...roleFormData,
                                        userContactMasked: e.target.checked
                                    })}
                                />
                                <Label check>Mask User Contact</Label>
                            </FormGroup>

                            <Label className="mb-3">Access Permissions</Label>
                            <div className="permission-grid">
                                <div className="permission-header mb-2">
                                    <div style={{width: '200px'}}></div>
                                    <div className="text-center" style={{width: '100px'}}>Read</div>
                                    <div className="text-center" style={{width: '100px'}}>Write</div>
                            </div>
                                {Object.entries(accessOptions).map(([key, label]) => (
                                    <div key={key} className="d-flex align-items-center mb-2 permission-row">
                                        <div style={{width: '200px'}}>
                                            <Label className="mb-0">{label}</Label>
                                        </div>
                                        <div className="d-flex justify-content-center" style={{width: '100px'}}>
                                            <Input
                                                type="checkbox"
                                                checked={roleFormData.access[key]?.[0] === '1'}
                                                onChange={(e) => {
                                                    const currentAccess = roleFormData.access[key] || '00';
                                                    setRoleFormData({
                                                        ...roleFormData,
                                                        access: {
                                                            ...roleFormData.access,
                                                            [key]: `${e.target.checked ? '1' : '0'}${currentAccess[1]}`
                                                        }
                                                    });
                                                }}
                                            />
                                        </div>
                                        <div className="d-flex justify-content-center" style={{width: '100px'}}>
                                            <Input
                                                type="checkbox"
                                                checked={roleFormData.access[key]?.[1] === '1'}
                                                onChange={(e) => {
                                                    // When write is checked, also enable read
                                                    // When write is unchecked, only disable write
                                                    const isChecking = e.target.checked;
                                                    setRoleFormData({
                                                        ...roleFormData,
                                                        access: {
                                                            ...roleFormData.access,
                                                            [key]: isChecking ? '11' : '10' // Set both to 1 when checking write, set only read to 1 when unchecking
                                                        }
                                                    });
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="text-end mt-4">
                                <Button color="light" onClick={() => setRoleModal(false)} className="me-2">
                                    Cancel
                                </Button>
                                <Button type="submit" color="primary" disabled={loading}>
                                    {loading ? <Spinner size="sm" /> : (selectedRole ? 'Update' : 'Create')}
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>

                {/* Reset Password Modal */}
                <Modal isOpen={resetPasswordModal} toggle={() => setResetPasswordModal(false)}>
                    <ModalHeader toggle={() => setResetPasswordModal(false)}>
                        Reset Password
                    </ModalHeader>
                    <ModalBody>
                        <Form onSubmit={handleResetPassword}>
                            <FormGroup>
                                <Label for="password">New Password</Label>
                                <Input
                                    type="password"
                                    id="password"
                                    value={passwords.password}
                                    onChange={(e) => setPasswords(prev => ({
                                        ...prev,
                                        password: e.target.value
                                    }))}
                                    placeholder="Enter new password"
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label for="confirmPassword">Confirm Password</Label>
                                <Input
                                    type="password"
                                    id="confirmPassword"
                                    value={passwords.confirmPassword}
                                    onChange={(e) => setPasswords(prev => ({
                                        ...prev,
                                        confirmPassword: e.target.value
                                    }))}
                                    placeholder="Confirm new password"
                                    required
                                />
                            </FormGroup>
                            <div className="text-end mt-4">
                                <Button 
                                    color="light" 
                                    onClick={() => setResetPasswordModal(false)}
                                    className="me-2"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit" 
                                    color="primary"
                                    disabled={resetLoading}
                                >
                                    {resetLoading ? <Spinner size="sm" /> : 'Reset Password'}
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>

                {/* Add these styles */}
                <style>
                    {`
                    .permission-grid {
                        max-height: 400px;
                        overflow-y: auto;
                        padding: 1rem;
                        border: 1px solid #dee2e6;
                        border-radius: 0.25rem;
                    }
                    .permission-header {
                        font-weight: bold;
                        border-bottom: 2px solid #dee2e6;
                        padding-bottom: 0.5rem;
                        display: flex;
                    }
                    .permission-row:hover {
                        background-color: #f8f9fa;
                    }
                    .search-box {
                        position: relative;
                        width: 250px;
                    }

                    .search-box .search-icon {
                        position: absolute;
                        right: 12px;
                        top: 50%;
                        transform: translateY(-50%);
                        color: #878a99;
                        z-index: 1;
                        pointer-events: none;
                    }

                    .search-box .form-control {
                        padding-right: 35px;
                        border-radius: 6px;
                    }

                    .search-box .form-control:focus {
                        border-color: #405189;
                        box-shadow: none;
                    }

                    .card-header .search-box {
                        position: relative;
                        width: 250px;
                    }

                    .card-header .search-box .form-control {
                        height: 36px;
                        font-size: 13.5px;
                        padding: 0.5rem 2.5rem 0.5rem 1rem;
                    }

                    .card-header .search-box .search-icon {
                        position: absolute;
                        right: 13px;
                        top: 50%;
                        transform: translateY(-50%);
                        font-size: 15px;
                        color: #878a99;
                    }

                    .card-header .d-flex.flex-wrap {
                        gap: 10px !important;
                    }

                    .btn-icon {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        height: 36px;
                        padding: 0 16px;
                        font-size: 13.5px;
                        font-weight: 500;
                    }

                    .btn-icon i {
                        font-size: 15px;
                    }

                    @media (max-width: 991.98px) {
                        .card-header .d-flex {
                            flex-direction: column;
                            align-items: stretch;
                        }

                        .card-header .search-box {
                            width: 100%;
                            margin-bottom: 1rem;
                        }

                        .card-header .d-flex.flex-wrap {
                            justify-content: flex-end;
                        }
                    }

                    @media (max-width: 575.98px) {
                        .card-header .d-flex.flex-wrap {
                            justify-content: stretch;
                            gap: 8px;
                        }

                        .btn-icon {
                            flex: 1;
                        }
                    }

                    .add-btn {
                        padding: 6px 14px;
                        font-size: 13.5px;
                        font-weight: 500;
                        border-radius: 4px;
                    }

                    @media (max-width: 767.98px) {
                        .card-header .d-flex {
                            flex-direction: column;
                            align-items: stretch;
                            gap: 1rem;
                        }

                        .card-header .search-box {
                            width: 100%;
                        }

                        .d-flex.flex-wrap.gap-2 {
                            justify-content: flex-start;
                        }
                    }

                    @media (max-width: 575.98px) {
                        .col-sm-auto {
                            width: 100%;
                        }

                        .d-flex.flex-wrap.gap-2 {
                            gap: 0.5rem !important;
                        }

                        .add-btn {
                            flex: 1;
                        }
                    }

                    .drag-over {
                        border-color: #405189 !important;
                        background-color: rgba(64, 81, 137, 0.05) !important;
                    }
                    `}
                </style>

                {loading && (
                    <div className="overlay">
                        <Spinner color="primary" />
                                    </div>
                                )}

                {/* Add Session Logs Modal */}
                <Modal
                    isOpen={sessionModal}
                    toggle={() => setSessionModal(false)}
                    size="xl"
                    className="modal-dialog-centered"
                >
                    <ModalHeader className="bg-light p-3" toggle={() => setSessionModal(false)}>
                        Session Logs
                        <p className="text-muted mb-0">View all session logs and their details</p>
                    </ModalHeader>
                    <ModalBody>
                        <div className="row g-3">
                            <div className="col-lg-12">
                                <div className="search-box">
                                    <input
                                        type="text"
                                        className="form-control search"
                                        placeholder="Search by action..."
                                        value={sessionSearch}
                                        onChange={(e) => setSessionSearch(e.target.value)}
                                    />
                                    <i className="ri-search-line search-icon"></i>
                                        </div>
                                    </div>
                        </div>

                        {loadingLogs ? (
                            <div className="text-center p-3">
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive table-card mt-3 mb-1">
                                    <Table className="align-middle table-nowrap">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col">Start Time</th>
                                                <th scope="col">End Time</th>
                                                <th scope="col">IP Address</th>
                                                <th scope="col">Action</th>
                                                        </tr>
                                        </thead>
                                        <tbody>
                                            {getFilteredSessionLogs().length > 0 ? (
                                                getFilteredSessionLogs().map(log => (
                                                    <tr key={log._id}>
                                                        <td>{moment(log.startDateTime).format('DD-MM-YYYY HH:mm:ss')}</td>
                                                        <td>{log.endDateTime ? moment(log.endDateTime).format('DD-MM-YYYY HH:mm:ss') : 'N/A'}</td>
                                                        <td>{log.ipAddress}</td>
                                                        <td>{log.action}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center">
                                                        {sessionSearch ? "No matching records found" : "No session logs available"}
                                                                </td>
                                                            </tr>
                                            )}
                                                        </tbody>
                                                    </Table>
                                                </div>

                                {Array.isArray(sessionLogs) && sessionLogs.length > pageSize && (
                                    <div className="d-flex justify-content-end mt-3">
                                        <div className="pagination-wrap hstack gap-2">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                            >
                                                Previous
                                            </Button>
                                            <span className="px-3 py-1 border rounded">
                                                Page {currentPage} of {Math.ceil(sessionLogs.length / pageSize)}
                                            </span>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => setCurrentPage(prev => 
                                                    Math.min(prev + 1, Math.ceil(sessionLogs.length / pageSize))
                                                )}
                                                disabled={currentPage === Math.ceil(sessionLogs.length / pageSize)}
                                            >
                                                Next
                                            </Button>
                                </div>
                            </div>
                                )}
                            </>
                        )}
                    </ModalBody>
                </Modal>

                {/* Add this at the end of your JSX, right before the ToastContainer */}
                {sweetAlert}

                {/* Add User Modal - Updated with profile image upload */}
                <Modal
                    isOpen={addUserModal}
                    toggle={() => setAddUserModal(false)}
                    centered
                    size="lg"
                >
                    <ModalHeader className="bg-light p-3" toggle={() => setAddUserModal(false)}>
                        Add New Admin User
                    </ModalHeader>
                    <Form onSubmit={handleAddUser}>
                        <ModalBody>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label for="username" className="form-label">Username</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            id="username"
                                            placeholder="Enter username"
                                            value={addUserForm.username}
                                            onChange={(e) => setAddUserForm({...addUserForm, username: e.target.value})}
                                            required
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="email" className="form-label">Email</Label>
                                        <Input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            placeholder="Enter email"
                                            value={addUserForm.email}
                                            onChange={(e) => setAddUserForm({...addUserForm, email: e.target.value})}
                                            required
                                        />
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="role" className="form-label">Role</Label>
                                        <Input
                                            type="select"
                                            className="form-select"
                                            id="role"
                                            value={addUserForm.roleId}
                                            onChange={(e) => setAddUserForm({...addUserForm, roleId: e.target.value})}
                                            required
                                        >
                                            <option value="">Select Role</option>
                                            {availableRoles.map(role => (
                                                <option key={role._id} value={role._id}>
                                                    {role.role}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label for="password" className="form-label">Password</Label>
                                        <Input
                                            type="password"
                                            className="form-control"
                                            id="password"
                                            placeholder="Enter password"
                                            value={addUserForm.password}
                                            onChange={(e) => setAddUserForm({...addUserForm, password: e.target.value})}
                                            required
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Profile Image</Label>
                                        <div 
                                            className="upload-zone p-3 text-center border rounded"
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                        >
                                            <div
                                                className="upload-area"
                                                onClick={() => fileInputRef.current.click()}
                                                style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: '#f8f9fa',
                                                    transition: 'all 0.3s ease',
                                                    padding: '1.5rem',
                                                    border: '2px dashed #dee2e6',
                                                    borderRadius: '8px',
                                                    minHeight: '200px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {uploadLoading ? (
                                                    <div className="text-center">
                                                        <Spinner color="primary" size="sm" />
                                                        <p className="mt-2 mb-0">Uploading image...</p>
                                                    </div>
                                                ) : imagePreview ? (
                                                    <div className="preview-container">
                                                        <div className="image-preview-wrapper position-relative">
                                                            <img
                                                                src={imagePreview}
                                                                alt="Profile Preview"
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    maxHeight: '150px',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '8px'
                                                                }}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = require('../../assets/images/users/avatar-1.jpg');
                                                                }}
                                                            />
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                className="position-absolute top-0 end-0 m-2"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedFile(null);
                                                                    setImagePreview(null);
                                                                    setAddUserForm({...addUserForm, image: ''});
                                                                    if (fileInputRef.current) {
                                                                        fileInputRef.current.value = '';
                                                                    }
                                                                }}
                                                            >
                                                                <i className="ri-close-line"></i>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <i className="ri-upload-cloud-2-line display-4 text-muted"></i>
                                                        <p className="mb-0">Click or drag to upload profile image</p>
                                                        <p className="text-muted small mb-0">
                                                            Supported formats: PNG, JPG, JPEG (Max 2MB)
                                                        </p>
                                                    </>
                                                )}
                                                <Input
                                                    type="file"
                                                    innerRef={fileInputRef}
                                                    className="d-none"
                                                    accept="image/*"
                                                    onChange={handleFileSelect}
                                                />
                                            </div>
                                        </div>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </ModalBody>
                        <div className="modal-footer">
                            <div className="hstack gap-2 justify-content-end">
                                <Button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={() => {
                                        setAddUserModal(false);
                                        setSelectedFile(null);
                                        setImagePreview(null);
                                        setAddUserForm({
                                            username: '',
                                            email: '',
                                            roleId: '',
                                            password: '',
                                            image: ''
                                        });
                                        if (fileInputRef.current) {
                                            fileInputRef.current.value = '';
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={addUserLoading || uploadLoading}
                                >
                                    {addUserLoading ? (
                                        <Spinner size="sm" className="me-2" />
                                    ) : null}
                                    Add User
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Modal>

                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                />
            </React.Fragment>
        );
    };

    export default AdminUsers;